import * as React from "react";
import type { ReactNode } from "react";

import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "@/shared/components/primitives/popover";

export interface ColocaPopoverProps {
  /**
   * Element that toggles the popover on click — typically a `<ColocaButton>` or `<ColocaIconButton>`. The
   * popover wires the click handler automatically; do not add your own `onClick` to the trigger.
   */
  trigger: React.ReactElement;
  /**
   * Optional heading rendered at the top of the panel. Use to orient the user when the content alone is not
   * self-explanatory — e.g. "Filter by date" or "Notification settings".
   */
  title?: ReactNode;
  /** Optional subtitle rendered below the title. Use for brief context or instructions. */
  description?: ReactNode;
  /** Popover body — forms, option lists, rich content, or any arbitrary JSX. */
  children?: ReactNode;
  /** Which side of the trigger the panel opens on. Defaults to `"bottom"`. */
  side?: "top" | "right" | "bottom" | "left";
  /** Alignment of the panel relative to the trigger edge. Defaults to `"center"`. */
  align?: "start" | "center" | "end";
  /** Gap in px between the trigger and the panel. Defaults to `4`. */
  sideOffset?: number;
  /**
   * Extra classes applied to the popup panel. Use to override the default `w-72` — e.g. `"w-80"` for filter
   * panels, `"w-56"` for compact option lists.
   */
  contentClassName?: string;
  /** Controlled open state. Pair with `onOpenChange` to drive the popover from outside. */
  open?: boolean;
  /** Uncontrolled initial open state. */
  defaultOpen?: boolean;
  /** Called when the popover opens or closes. */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Use `ColocaPopover` for non-modal overlays triggered by click — filter panels, inline editors, option
 * pickers, or any rich content that should appear contextually without blocking the page.
 *
 * For destructive confirmations use `ColocaAlertDialog`. For hover labels use `ColocaTooltip`.
 *
 * @summary for click-triggered contextual panels that do not block the page
 */
export function ColocaPopover({
  trigger,
  title,
  description,
  children,
  side,
  align,
  sideOffset,
  contentClassName,
  open,
  defaultOpen,
  onOpenChange,
}: ColocaPopoverProps) {
  return (
    <Popover open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger render={trigger} />
      <PopoverContent side={side} align={align} sideOffset={sideOffset} className={contentClassName}>
        {(title || description) && (
          <PopoverHeader>
            {title && <PopoverTitle>{title}</PopoverTitle>}
            {description && <PopoverDescription>{description}</PopoverDescription>}
          </PopoverHeader>
        )}
        {children}
      </PopoverContent>
    </Popover>
  );
}
